import { MoreVertical } from 'lucide-react';
import { useEffect, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import type { DRDaySummary, DRDayFixture, DRCardDetails, DRUserEntry } from '../../api/getMyGames';
import { FitText } from '../common/FitText';
import { resolveMlbStylePlayerImage, hitOutcomeIconUrl } from '../../utils/dailyRevealPlayerImage';
import {
  formatLobbyPropResultLabel,
  isPropBasedEntry,
  resolveLobbyPropPickParts,
  resolvePropPickLine,
} from '../../utils/dailyRevealPickLabel';
import { formatDailyRevealContestTitle } from '../../utils/dailyRevealContestTitle';
import { getMyGamesSportLogoPath } from '../../utils/myGamesSportLogo';
import type { TeamAssetFolderId } from '../../utils/teamAssetFolder';
import { navigateToGameUrl } from '../../utils/lobbyMyGamesNavigation';
import { resolveTeamAbbreviation, toTeamAbbreviation } from '../../utils/teamAbbreviation';

/** Matches FitText floors used on Daily Reveal home cards, sized for the My Games column. */
const PLAYER_NAME_MAX_FONT_SIZE = 16;
const PLAYER_NAME_MIN_FONT_SIZE = 11;
/** Prop line sits beside the scoreboard — keep it compact to avoid overlap. */
const PROP_LINE_FONT_SIZE = '11px';

function useCardNavigation(targetUrl?: string | null) {
  const handleNavigate = (e?: MouseEvent | KeyboardEvent) => {
    if (!targetUrl) return;
    e?.preventDefault();
    navigateToGameUrl(targetUrl);
  };

  const interactive = Boolean(targetUrl);

  return {
    handleNavigate,
    interactive,
    cardClassName: interactive
      ? 'cursor-pointer transition-all hover:ring-2 hover:ring-[#53d337]/30 active:scale-[0.98]'
      : 'cursor-default',
  };
}

const NAME_SUFFIXES = new Set(['jr', 'jr.', 'sr', 'sr.', 'ii', 'iii', 'iv', 'v']);

function playerInitials(card: DRCardDetails): string {
  const first = card.firstName?.trim() ?? '';
  const last = card.lastName?.trim() ?? '';
  if (first || last) return `${first[0] ?? ''}${last[0] ?? ''}`.toUpperCase();
  const parts = (card.displayName ?? '').trim().split(/\s+/);
  if (parts.length <= 1) return (parts[0]?.[0] ?? '?').toUpperCase();
  const lastToken = parts[parts.length - 1];
  const surname =
    parts.length >= 3 && NAME_SUFFIXES.has(lastToken.toLowerCase())
      ? parts[parts.length - 2]
      : lastToken;
  return `${parts[0][0] ?? ''}${surname[0] ?? ''}`.toUpperCase();
}

/** "Drake Maye" → "D. MAYE" — same shape as Daily Reveal home / My Games cards. */
function formatAbbreviatedPlayerName(card: DRCardDetails): string {
  const displayName = card.displayName ?? '';
  const first = (card.firstName || displayName.split(/\s+/)[0] || '').trim();
  const last = (card.lastName || displayName.split(/\s+/).slice(1).join(' ') || '').trim();
  if (first && last) return `${first.charAt(0)}. ${last}`.toUpperCase();
  return (displayName || `${first} ${last}`.trim()).toUpperCase();
}

function formatFixtureStatus(fixture: DRDayFixture): string {
  if (fixture.status === 'live') return 'Live';
  if (fixture.status === 'finished') return 'Final';
  if (fixture.status === 'postponed') return 'Postponed';
  if (fixture.status === 'cancelled') return 'Cancelled';
  if (fixture.startTime) {
    return new Date(fixture.startTime).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  }
  return '';
}

function findFixtureForCard(
  teamExternalId: string | null | undefined,
  fixtures: DRDayFixture[]
): DRDayFixture | null {
  if (!teamExternalId || fixtures.length === 0) return null;
  return (
    fixtures.find(f => f.homeTeamId === teamExternalId || f.awayTeamId === teamExternalId) ?? null
  );
}

function formatCountdown(ms: number): string {
  if (ms <= 0) return '00:00:00';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return [String(h).padStart(2, '0'), String(m).padStart(2, '0'), String(s).padStart(2, '0')].join(
    ':'
  );
}

type ContestStartState = 'countdown' | 'live' | 'final' | 'upcoming';

function resolveContestStartState(
  fixture: DRDayFixture | null,
  isGraded: boolean
): ContestStartState {
  if (isGraded || fixture?.status === 'finished') return 'final';
  if (fixture?.status === 'live') return 'live';
  if (fixture?.startTime) {
    const diff = new Date(fixture.startTime).getTime() - Date.now();
    if (diff <= 0) return 'live';
    if (diff <= 86_400_000) return 'countdown';
  }
  return 'upcoming';
}

function useContestStartDisplay(
  fixture: DRDayFixture | null,
  isGraded: boolean
): { state: ContestStartState; label: string } {
  const state = resolveContestStartState(fixture, isGraded);
  const startMs = fixture?.startTime ? new Date(fixture.startTime).getTime() : null;

  const [countdown, setCountdown] = useState(() =>
    startMs != null ? formatCountdown(startMs - Date.now()) : '00:00:00'
  );

  useEffect(() => {
    if (state !== 'countdown' || startMs == null) return;
    const tick = () => setCountdown(formatCountdown(startMs - Date.now()));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [state, startMs]);

  if (state === 'countdown') return { state, label: countdown };
  if (state === 'live') return { state, label: 'LIVE' };
  if (state === 'final') return { state, label: 'Complete' };

  if (startMs != null) {
    const days = Math.floor((startMs - Date.now()) / 86_400_000);
    if (days > 0) return { state, label: `${days} Day${days > 1 ? 's' : ''}` };
  }
  return { state, label: 'Soon' };
}

// The prop progress tracker below is ported from daily-reveal's PropStatProgressTracker.

const TARGET_AT_FRACTION = 0.8;

type PropTrackerOutcome = 'pending' | 'hit' | 'miss' | 'void';

function formatPropStatNumber(value: number): string {
  if (!Number.isFinite(value)) return '0';
  if (Number.isInteger(value) || Math.abs(value - Math.round(value)) < 1e-9) {
    return String(Math.round(value));
  }
  return String(Number(value.toFixed(1)));
}

function getPropProgressPositions(
  currentValue: number,
  threshold: number
): { fillPct: number; bubblePct: number; targetPct: number } {
  const safeThreshold = Math.max(threshold, 0.01);
  const maxScale = safeThreshold / TARGET_AT_FRACTION;
  const rawPct = (Math.max(0, currentValue) / maxScale) * 100;
  const clamped = Math.min(100, rawPct);
  return {
    fillPct: clamped,
    bubblePct: clamped,
    targetPct: TARGET_AT_FRACTION * 100,
  };
}

function resolvePropThreshold(entry: DRUserEntry): number | null {
  if (entry.propThreshold != null && Number.isFinite(Number(entry.propThreshold))) {
    return Number(entry.propThreshold);
  }
  const label = entry.propDisplayLabel?.trim() ?? '';
  const match = label.match(/^([0-9]+(?:\.[0-9]+)?)\+/);
  if (!match) return null;
  const value = Number(match[1]);
  return Number.isFinite(value) ? value : null;
}

function resolvePropTrackerOutcome(
  entry: DRUserEntry,
  threshold: number | null,
  isGraded: boolean,
  isLiveOrFinished: boolean
): PropTrackerOutcome {
  const status = entry.propResultStatus;
  if (status === 'won' || status === 'credited') return 'hit';
  if (status === 'lost') return 'miss';
  if (status === 'void') return 'void';

  if (threshold != null && (isLiveOrFinished || isGraded)) {
    const value = Number(entry.propResultValue ?? 0);
    // Frozen lobby lines are overs / N+ — treat equal-or-over as a hit.
    return value >= threshold ? 'hit' : 'miss';
  }
  return 'pending';
}

function LobbyPropProgressTracker({
  currentValue,
  threshold,
  outcome,
}: {
  currentValue: number | null | undefined;
  threshold: number;
  outcome: PropTrackerOutcome;
}) {
  const current =
    currentValue != null && Number.isFinite(Number(currentValue)) ? Number(currentValue) : 0;
  const { fillPct, bubblePct, targetPct } = getPropProgressPositions(current, threshold);

  const isHit = outcome === 'hit';
  const isMiss = outcome === 'miss';
  const accent = isHit ? '#53D337' : isMiss ? '#E9344A' : '#8a8a8a';
  const bubbleBg = isHit ? '#53D337' : isMiss ? '#E9344A' : '#4a4a4a';

  return (
    <div className="relative w-full pt-1 pb-4 pl-1">
      <div className="relative h-[6px] w-full rounded-full bg-[#3a3a3a]">
        <div
          className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-500 ease-out"
          style={{ width: `${fillPct}%`, backgroundColor: accent }}
        />
        <div
          className="pointer-events-none absolute top-1/2 z-[1] -translate-x-1/2 -translate-y-1/2"
          style={{ left: `${targetPct}%` }}
          aria-hidden
        >
          <div className="h-3 w-0 border-l-[1.5px] border-dashed border-white/90" />
        </div>
        <div
          className="absolute top-1/2 z-[2] -translate-x-1/2 -translate-y-1/2 transition-[left] duration-500 ease-out"
          style={{ left: `${bubblePct}%` }}
        >
          <span
            className="inline-flex min-w-[28px] items-center justify-center rounded-full px-1.5 py-[2px] text-[11px] font-bold leading-none tabular-nums text-white shadow-sm"
            style={{ backgroundColor: bubbleBg }}
          >
            {formatPropStatNumber(current)}
          </span>
        </div>
      </div>
      <div
        className="pointer-events-none absolute bottom-0 -translate-x-1/2 text-[10px] font-medium leading-none tabular-nums text-white/90"
        style={{ left: `${targetPct}%` }}
      >
        {formatPropStatNumber(threshold)}
      </div>
    </div>
  );
}

/** Card shell matching the Pick'em / Squares / Survivor My Games cards. */
function DailyRevealContestShell({
  sport,
  title,
  startState,
  startLabel,
  targetUrl,
  children,
}: {
  sport: string | null | undefined;
  title: string;
  startState: ContestStartState;
  startLabel: string;
  targetUrl?: string | null;
  children: ReactNode;
}) {
  const logoPath = getMyGamesSportLogoPath(sport ?? undefined);
  const { handleNavigate, interactive, cardClassName } = useCardNavigation(targetUrl);
  const isLive = startState === 'live';

  return (
    <div
      role={interactive ? 'link' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-disabled={!interactive}
      onClick={handleNavigate}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') handleNavigate(e);
      }}
      className={`mb-3 block rounded-xl border bg-[#242424] p-4 transition-all duration-200 ${
        isLive ? 'border-2 border-[#53d337]' : 'border border-[#373737]'
      } ${cardClassName}`}
    >
      <div className="mb-4 flex items-center justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[#373737]">
            {logoPath ? (
              <img
                src={logoPath}
                alt={`${sport ?? ''} logo`}
                className="h-full w-full object-contain p-1"
              />
            ) : (
              <span className="text-[10px] font-medium text-zinc-400">Logo</span>
            )}
          </div>
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="truncate text-sm font-semibold uppercase leading-tight tracking-wide text-white">
              {title}
            </span>
            <div className="mt-0.5 flex items-center gap-2">
              <span className="text-sm font-normal text-zinc-500">Start:</span>
              <span
                className={`text-sm font-semibold ${
                  startState === 'final' ? 'text-zinc-400' : 'text-[#53d337]'
                }`}
              >
                {startLabel}
              </span>
              <span className="text-sm font-normal text-zinc-500">(1 Game)</span>
            </div>
          </div>
        </div>
        <MoreVertical className="h-5 w-5 flex-shrink-0 text-zinc-400" aria-hidden />
      </div>

      {children}
    </div>
  );
}

function PlayerAvatar({
  displayImageUrl,
  displayName,
  initials,
}: {
  displayImageUrl: string | null;
  displayName: string;
  initials: string;
}) {
  return (
    <div className="flex h-[52px] w-[52px] flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[#2a2a2a]">
      {displayImageUrl ? (
        <img src={displayImageUrl} alt={displayName} className="h-full w-full object-contain" />
      ) : (
        <span className="text-lg font-bold text-white/30">{initials}</span>
      )}
    </div>
  );
}

function ScoreboardColumn({
  fixture,
  awayTeam,
  homeTeam,
  isLive,
  gameStatus,
}: {
  fixture: DRDayFixture | null;
  awayTeam: string;
  homeTeam: string;
  isLive: boolean;
  gameStatus: string;
}) {
  return (
    <div className="flex w-[100px] shrink-0 flex-col items-end">
      <p
        className={`mb-2 ${isLive ? 'text-[#53d337]' : 'text-[#8a8a8a]'}`}
        style={{ fontWeight: 500, fontSize: '9px' }}
      >
        {isLive && <span className="mr-1 inline-block h-[6px] w-[6px] rounded-full bg-[#53d337]" />}
        {gameStatus}
      </p>
      <div className="grid grid-cols-[18px_1fr_auto] gap-x-2 gap-y-1 text-sm font-semibold">
        {awayTeam && (
          <>
            <div className="flex h-[18px] w-[18px] shrink-0 items-center justify-center" />
            <span className="min-w-0 truncate text-right uppercase text-[#ffffff]">{awayTeam}</span>
            <span className="w-6 text-right tabular-nums text-[#ffffff]">
              {fixture?.awayScore != null ? fixture.awayScore : '-'}
            </span>
          </>
        )}
        {homeTeam && (
          <>
            <div className="flex h-[18px] w-[18px] shrink-0 items-center justify-center" />
            <span className="min-w-0 truncate text-right uppercase text-[#ffffff]">{homeTeam}</span>
            <span className="w-6 text-right tabular-nums text-[#ffffff]">
              {fixture?.homeScore != null ? fixture.homeScore : '-'}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

interface TodaysPickCardProps {
  day: DRDaySummary;
  fixtures: DRDayFixture[];
  /** Contest sport — drives league logo + product title (Gridiron Draw / Big League Draw). */
  sport?: string | null;
  leagueIpEnabled?: boolean;
  teamAssetFolder?: TeamAssetFolderId | null;
  targetUrl?: string | null;
}

/** Today's pick card (Upcoming / Live tabs). */
export function LobbyDailyRevealTodaysPick({
  day,
  fixtures,
  sport = null,
  leagueIpEnabled = false,
  teamAssetFolder = null,
  targetUrl = null,
}: TodaysPickCardProps) {
  const card = day.userEntry?.selectedCard ?? null;
  const entry = day.userEntry;
  const isGraded = entry?.status === 'graded';
  const fixture = findFixtureForCard(card?.teamExternalId, fixtures);
  const { state: startState, label: startLabel } = useContestStartDisplay(
    fixture,
    Boolean(isGraded)
  );

  if (!card || !entry) return null;

  const totalHits = entry.totalHits ?? 0;
  const teamCode = resolveTeamAbbreviation(card.teamAbbreviation, card.teamName);

  const awayTeam = fixture?.awayTeam ? toTeamAbbreviation(fixture.awayTeam) : '';
  const homeTeam = fixture?.homeTeam ? toTeamAbbreviation(fixture.homeTeam) : teamCode;
  const displayImageUrl = resolveMlbStylePlayerImage(
    card,
    teamCode,
    leagueIpEnabled,
    teamAssetFolder
  );
  const initials = playerInitials(card);
  const abbreviatedName = formatAbbreviatedPlayerName(card);
  const isLive = fixture?.status === 'live';
  const gameStatus = fixture ? formatFixtureStatus(fixture) : (card.position ?? '');

  const hasLiveHits = !isGraded && isLive && totalHits > 0;
  const borderColor = isGraded
    ? totalHits > 0
      ? 'border-[#53D337]'
      : 'border-[#E9344A]'
    : hasLiveHits
      ? 'border-[#53D337]'
      : 'border-[#373737]';

  const propParts = resolveLobbyPropPickParts(entry);
  const isPropBased = isPropBasedEntry(entry);
  const propLine = resolvePropPickLine(entry);
  const threshold = resolvePropThreshold(entry);
  const isLiveOrFinished = fixture?.status === 'live' || fixture?.status === 'finished' || isGraded;
  const trackerOutcome = resolvePropTrackerOutcome(entry, threshold, isGraded, isLiveOrFinished);
  const showPropTracker =
    isPropBased &&
    threshold != null &&
    threshold > 0 &&
    (isLiveOrFinished || entry.propResultValue != null);

  const hitLabel = isPropBased
    ? (propLine ?? 'Pending')
    : isGraded
      ? totalHits > 0
        ? `${totalHits} HIT${totalHits !== 1 ? 'S' : ''}`
        : '0 HITS'
      : totalHits > 0
        ? `${totalHits} HIT${totalHits !== 1 ? 'S' : ''}`
        : '1+ HIT';

  const hitColor = isPropBased
    ? 'text-[#ffffff]'
    : isGraded
      ? totalHits > 0
        ? 'text-[#ffffff]'
        : 'text-[#EA2054]'
      : 'text-[#ffffff]';

  const title = formatDailyRevealContestTitle(sport, day.gameDate);
  const statusCorner = isLive
    ? 'Live'
    : fixture?.status === 'finished' || isGraded
      ? 'Final'
      : gameStatus;

  return (
    <DailyRevealContestShell
      sport={sport}
      title={title}
      startState={startState}
      startLabel={startLabel}
      targetUrl={targetUrl}
    >
      {showPropTracker && threshold != null ? (
        <div
          className={`relative rounded-[10px] border-2 bg-[#1a1a1a] px-4 pb-2 pt-3 ${borderColor}`}
        >
          <p
            className={`absolute right-4 top-3 text-[11px] font-medium uppercase tracking-wide leading-none ${
              isLive ? 'text-[#53d337]' : 'text-[#9a9a9a]'
            }`}
          >
            {statusCorner}
          </p>
          <div className="flex items-start gap-3.5 pr-12">
            <PlayerAvatar
              displayImageUrl={displayImageUrl}
              displayName={card.displayName}
              initials={initials}
            />
            <div className="min-w-0 flex-1">
              <FitText
                text={abbreviatedName}
                maxFontSize={PLAYER_NAME_MAX_FONT_SIZE}
                minFontSize={PLAYER_NAME_MIN_FONT_SIZE}
                className="font-bold uppercase leading-tight text-[#ffffff]"
              />
              <div className="mt-2 flex items-center gap-2">
                {(trackerOutcome === 'hit' || trackerOutcome === 'miss') && (
                  <img
                    src={hitOutcomeIconUrl(trackerOutcome === 'hit')}
                    alt={trackerOutcome === 'hit' ? 'Correct' : 'Incorrect'}
                    className="h-6 w-6 shrink-0"
                  />
                )}
                {propParts ? (
                  <p
                    className="min-w-0 truncate uppercase leading-none"
                    style={{
                      fontWeight: 700,
                      fontSize: PROP_LINE_FONT_SIZE,
                      letterSpacing: '0.02em',
                    }}
                  >
                    <span className="text-white">
                      {propParts.value.replace(/^\+/, '')}
                      {propParts.value ? '+' : ''}
                    </span>
                    {propParts.category ? (
                      <>
                        {' '}
                        <span className="text-[#BCBCBC]">{propParts.category}</span>
                      </>
                    ) : null}
                  </p>
                ) : null}
              </div>
              <div className="mt-3">
                <LobbyPropProgressTracker
                  currentValue={entry.propResultValue}
                  threshold={threshold}
                  outcome={trackerOutcome}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className={`rounded-[10px] border-2 bg-[#1a1a1a] px-4 py-3 ${borderColor}`}>
          <div className="flex items-center">
            <div className="flex min-w-0 flex-1 items-center gap-4">
              <PlayerAvatar
                displayImageUrl={displayImageUrl}
                displayName={card.displayName}
                initials={initials}
              />
              <div className="min-w-0 flex-1 overflow-hidden">
                <FitText
                  text={abbreviatedName}
                  maxFontSize={PLAYER_NAME_MAX_FONT_SIZE}
                  minFontSize={PLAYER_NAME_MIN_FONT_SIZE}
                  className="font-bold uppercase leading-tight text-[#ffffff]"
                />
                <p className="mt-0.5 truncate text-xs font-medium text-[#8a8a8a]">
                  {teamCode}
                  {card.jerseyNumber ? ` • ${card.jerseyNumber}` : ''}
                  {card.position ? ` • ${card.position}` : ''}
                </p>
                <div className="mt-2 flex min-w-0 items-center gap-2">
                  {propParts?.category ? (
                    <p
                      className="min-w-0 truncate uppercase leading-none"
                      style={{
                        fontWeight: 700,
                        fontSize: PROP_LINE_FONT_SIZE,
                        letterSpacing: '0.02em',
                      }}
                    >
                      <span className="uppercase text-[#ffffff]">{propParts.value}</span>
                      {propParts.value ? ' ' : ''}
                      <span className="text-[#BCBCBC]">{propParts.category}</span>
                    </p>
                  ) : (
                    <span
                      className={`whitespace-nowrap uppercase ${hitColor}`}
                      style={{ fontWeight: 700, fontSize: PROP_LINE_FONT_SIZE }}
                    >
                      {hitLabel}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div
              className="mx-4 w-px shrink-0 self-stretch"
              style={{ backgroundColor: '#868686' }}
              aria-hidden
            />
            <ScoreboardColumn
              fixture={fixture}
              awayTeam={awayTeam}
              homeTeam={homeTeam}
              isLive={isLive}
              gameStatus={gameStatus}
            />
          </div>
        </div>
      )}
    </DailyRevealContestShell>
  );
}

interface WeeklySelectionCardProps {
  day: DRDaySummary;
  fixtures: DRDayFixture[];
  sport?: string | null;
  leagueIpEnabled?: boolean;
  teamAssetFolder?: TeamAssetFolderId | null;
  targetUrl?: string | null;
}

/** Weekly selection card (Recent tab). */
export function LobbyDailyRevealWeeklyCard({
  day,
  fixtures,
  sport = null,
  leagueIpEnabled = false,
  teamAssetFolder = null,
  targetUrl = null,
}: WeeklySelectionCardProps) {
  const card = (day.userEntry?.selectedCard as DRCardDetails | null | undefined) ?? null;
  const entry = day.userEntry;
  const isGraded = entry?.status === 'graded';
  const fixture = findFixtureForCard(card?.teamExternalId, fixtures);
  const { state: startState, label: startLabel } = useContestStartDisplay(
    fixture,
    Boolean(isGraded)
  );

  if (!card || !entry) return null;

  const totalHits = entry.totalHits ?? 0;
  const won = Boolean(isGraded) && totalHits > 0;
  const teamCode = resolveTeamAbbreviation(card.teamAbbreviation, card.teamName);

  const awayTeam = fixture?.awayTeam ? toTeamAbbreviation(fixture.awayTeam) : '';
  const homeTeam = fixture?.homeTeam ? toTeamAbbreviation(fixture.homeTeam) : teamCode;
  const displayImageUrl = resolveMlbStylePlayerImage(
    card,
    teamCode,
    leagueIpEnabled,
    teamAssetFolder
  );
  const initials = playerInitials(card);
  const abbreviatedName = formatAbbreviatedPlayerName(card);
  const isLive = fixture?.status === 'live';
  const isFinishedNoHits = !isGraded && totalHits === 0 && fixture?.status === 'finished';
  const gameStatus = fixture ? formatFixtureStatus(fixture) : '';
  const hasLiveHits = !isGraded && isLive && totalHits > 0;

  const isPropBased = isPropBasedEntry(entry);
  const propLine = resolvePropPickLine(entry);
  const propResult = isPropBased ? formatLobbyPropResultLabel(entry, propLine ?? 'Pending') : null;
  const propParts = isPropBased ? resolveLobbyPropPickParts(entry) : null;
  const threshold = resolvePropThreshold(entry);
  const isLiveOrFinished = fixture?.status === 'live' || fixture?.status === 'finished' || isGraded;
  const trackerOutcome = resolvePropTrackerOutcome(entry, threshold, isGraded, isLiveOrFinished);
  const showPropTracker =
    isPropBased &&
    threshold != null &&
    threshold > 0 &&
    (isLiveOrFinished || entry.propResultValue != null);

  const propWon = isPropBased ? (propResult?.won ?? false) : won;
  const borderColor = isGraded
    ? propWon
      ? 'border-[#53D337]'
      : 'border-[#E9344A]'
    : hasLiveHits || trackerOutcome === 'hit'
      ? 'border-[#53D337]'
      : 'border-[#373737]';

  const hitLabel = isPropBased
    ? (propResult?.label ?? 'Pending')
    : isGraded
      ? totalHits > 0
        ? `${totalHits} HIT${totalHits !== 1 ? 'S' : ''}`
        : '0 HITS'
      : isFinishedNoHits
        ? '0 HITS'
        : totalHits > 0
          ? `${totalHits} HIT${totalHits !== 1 ? 'S' : ''}`
          : 'Pending';

  const hitColor = isPropBased
    ? isGraded
      ? propWon
        ? 'text-[#53d337]'
        : entry.propResultStatus === 'void'
          ? 'text-[#8a8a8a]'
          : 'text-[#EA2054]'
      : 'text-[#8a8a8a]'
    : isGraded
      ? won
        ? 'text-[#53d337]'
        : 'text-[#EA2054]'
      : isFinishedNoHits
        ? 'text-[#EA2054]'
        : totalHits > 0
          ? 'text-[#53d337]'
          : 'text-[#8a8a8a]';

  const showHitIcon = showPropTracker
    ? trackerOutcome === 'hit' || trackerOutcome === 'miss'
    : isPropBased
      ? Boolean(propResult?.showIcon)
      : hitLabel !== 'Pending';
  const isPositiveHitOutcome = showPropTracker
    ? trackerOutcome === 'hit'
    : isPropBased
      ? propWon
      : isGraded
        ? won
        : totalHits > 0;

  const title = formatDailyRevealContestTitle(sport, day.gameDate);
  const statusCorner = isLive
    ? 'Live'
    : fixture?.status === 'finished' || isGraded
      ? 'Final'
      : gameStatus;

  return (
    <DailyRevealContestShell
      sport={sport}
      title={title}
      startState={startState}
      startLabel={startLabel}
      targetUrl={targetUrl}
    >
      {showPropTracker && threshold != null ? (
        <div
          className={`relative rounded-[10px] border-2 bg-[#1a1a1a] px-4 pb-2 pt-3 ${borderColor}`}
        >
          <p
            className={`absolute right-4 top-3 text-[11px] font-medium uppercase tracking-wide leading-none ${
              isLive ? 'text-[#53d337]' : 'text-[#9a9a9a]'
            }`}
          >
            {statusCorner}
          </p>
          <div className="flex items-start gap-3.5 pr-12">
            <PlayerAvatar
              displayImageUrl={displayImageUrl}
              displayName={card.displayName}
              initials={initials}
            />
            <div className="min-w-0 flex-1">
              <FitText
                text={abbreviatedName}
                maxFontSize={PLAYER_NAME_MAX_FONT_SIZE}
                minFontSize={PLAYER_NAME_MIN_FONT_SIZE}
                className="font-bold uppercase leading-tight text-[#ffffff]"
              />
              <div className="mt-2 flex items-center gap-2">
                {showHitIcon && (
                  <img
                    src={hitOutcomeIconUrl(isPositiveHitOutcome)}
                    alt={isPositiveHitOutcome ? 'Correct' : 'Incorrect'}
                    className="h-6 w-6 shrink-0"
                  />
                )}
                {propParts ? (
                  <p
                    className="min-w-0 truncate uppercase leading-none"
                    style={{
                      fontWeight: 700,
                      fontSize: PROP_LINE_FONT_SIZE,
                      letterSpacing: '0.02em',
                    }}
                  >
                    <span className="text-white">
                      {propParts.value.replace(/^\+/, '')}
                      {propParts.value ? '+' : ''}
                    </span>
                    {propParts.category ? (
                      <>
                        {' '}
                        <span className="text-[#BCBCBC]">{propParts.category}</span>
                      </>
                    ) : null}
                  </p>
                ) : null}
              </div>
              <div className="mt-3">
                <LobbyPropProgressTracker
                  currentValue={entry.propResultValue}
                  threshold={threshold}
                  outcome={trackerOutcome}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className={`rounded-[10px] border-2 bg-[#1a1a1a] px-4 py-3 ${borderColor}`}>
          <div className="flex items-center">
            <div className="flex min-w-0 flex-1 items-center gap-4">
              <PlayerAvatar
                displayImageUrl={displayImageUrl}
                displayName={card.displayName}
                initials={initials}
              />
              <div className="min-w-0 flex-1 overflow-hidden">
                <FitText
                  text={abbreviatedName}
                  maxFontSize={PLAYER_NAME_MAX_FONT_SIZE}
                  minFontSize={PLAYER_NAME_MIN_FONT_SIZE}
                  className="font-bold uppercase leading-tight text-[#ffffff]"
                />
                <p className="mt-0.5 truncate text-xs font-medium text-[#8a8a8a]">
                  {teamCode}
                  {card.jerseyNumber ? ` • ${card.jerseyNumber}` : ''}
                  {card.position ? ` • ${card.position}` : ''}
                </p>
                <div className="mt-2 flex min-w-0 items-start gap-2">
                  {showHitIcon && (
                    <img
                      src={hitOutcomeIconUrl(isPositiveHitOutcome)}
                      alt={isPositiveHitOutcome ? 'Correct' : 'Incorrect'}
                      className="mt-0.5 h-6 w-6 shrink-0"
                    />
                  )}
                  {isPropBased && isGraded && propResult?.showIcon ? (
                    <div className="min-w-0 leading-none">
                      <p
                        className={`font-bold uppercase ${hitColor}`}
                        style={{ fontSize: PROP_LINE_FONT_SIZE, letterSpacing: '0.02em' }}
                      >
                        {propResult.statusLabel}
                      </p>
                      {propResult.propLine ? (
                        <p
                          className="mt-1 uppercase text-[#BCBCBC]"
                          style={{
                            fontWeight: 700,
                            fontSize: PROP_LINE_FONT_SIZE,
                            letterSpacing: '0.02em',
                          }}
                        >
                          {propResult.propLine}
                        </p>
                      ) : null}
                    </div>
                  ) : propParts?.category ? (
                    <p
                      className="min-w-0 uppercase leading-none"
                      style={{
                        fontWeight: 700,
                        fontSize: PROP_LINE_FONT_SIZE,
                        letterSpacing: '0.02em',
                      }}
                    >
                      <span className="uppercase text-[#ffffff]">{propParts.value}</span>
                      {propParts.value ? ' ' : ''}
                      <span className="text-[#BCBCBC]">{propParts.category}</span>
                    </p>
                  ) : (
                    <span
                      className={`relative -top-[1px] font-bold uppercase ${hitColor}`}
                      style={{ fontSize: PROP_LINE_FONT_SIZE }}
                    >
                      {hitLabel}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div
              className="mx-4 w-px shrink-0 self-stretch"
              style={{ backgroundColor: '#868686' }}
              aria-hidden
            />
            <ScoreboardColumn
              fixture={fixture}
              awayTeam={awayTeam}
              homeTeam={homeTeam}
              isLive={isLive}
              gameStatus={gameStatus}
            />
          </div>
        </div>
      )}
    </DailyRevealContestShell>
  );
}
